import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, IconButton } from '@mui/material';
import PlayArrow from '@mui/icons-material/PlayArrow';
import VolumeOffOutlined from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpOutlined from '@mui/icons-material/VolumeUpOutlined';
import Hls from 'hls.js';

import { RankBadge } from '../RankBadge';
import { VideoInfoLine } from '../VideoInfoLine';
import { VideoStatusPill } from '../VideoStatusPill';
import { Video } from '../../types';
import styles from './style';

const CLOUDFRONT_URL = process.env.REACT_APP_AWS_CLOUDFRONT_URL;
const HLS_TYPE = 'application/vnd.apple.mpegurl';
// The quality follows the size of the player and only a few seconds are downloaded ahead.
const HLS_CONFIG = {
  capLevelToPlayerSize: true,
  startLevel: -1,
  maxBufferLength: 12,
  maxMaxBufferLength: 20,
  backBufferLength: 30,
};
// A preloaded neighbour (feed) only keeps the first seconds, enough to start at once.
const PRELOAD_BUFFER_LENGTH = 4;
const PRELOAD_MAX_BUFFER_LENGTH = 6;
// Story mode: a press held this long pauses the video until it is released.
const HOLD_DELAY = 300;

// Story mode: the left third of the video goes back, the rest goes on.
type PlayerZone = 'previous' | 'next';

interface FeedPlayerProps {
  video: Video;
  muted: boolean;
  onToggleMute: () => void;
  // The browser refused the sound: the player had to mute itself (the saved choice is kept).
  onForcedMute: () => void;
  onOpenUser: () => void;
  // Story mode (ranking): a click goes to the previous / next video instead of pausing, a long press pauses.
  onZoneClick?: (zone: PlayerZone) => void;
  // The feed loops, a story goes on to the next video when it ends.
  loop?: boolean;
  onEnded?: () => void;
  // Drawn over the video with the progress (0 to 1), instead of the status pill ("Top N", "En compétition").
  renderOverlay?: (progress: number) => React.ReactNode;
  // Feed: false for a preloaded neighbour, paused at the start and muted until it becomes the current one.
  active?: boolean;
}

// Vertical player of the feed, dark until the video plays (no sharp poster). A landscape video is
// centered on a blurred copy of its thumbnail.
const FeedPlayer: React.FC<FeedPlayerProps> = React.memo((props) => {
  const {
    video,
    muted,
    onToggleMute,
    onForcedMute,
    onOpenUser,
    onZoneClick,
    loop = true,
    onEnded,
    renderOverlay,
    active = true,
  } = props;
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  // canplay comes again after a seek: only the first one starts the video.
  const startedRef = useRef(false);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The click that ends a long press must not change the video.
  const heldRef = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active;
  const hlsRef = useRef<Hls | null>(null);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  // Until the first frame plays (or while the stream stalls): a spinner over the dark background (no poster).
  const [buffering, setBuffering] = useState(true);

  const isLandscape = video.width > video.height;
  const videoUrl = `${CLOUDFRONT_URL}${video.path}`;
  // Old videos have no HLS stream: the mp4 is played.
  const streamUrl = video.stream ? `${CLOUDFRONT_URL}${video.stream}` : '';
  const thumbnailUrl = video.thumbnail ? `${CLOUDFRONT_URL}${video.thumbnail}` : '';

  // Another video starts from the beginning.
  useEffect(() => {
    startedRef.current = false;
    setPaused(false);
    setProgress(0);
    setBuffering(true);
  }, [video.id]);

  // The stream is attached once the keyed video element is mounted. A broken stream falls back to the mp4 once.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) {
      return undefined;
    }
    let hls: Hls | null = null;
    let fellBack = false;

    const fallBack = () => {
      if (fellBack) {
        return;
      }
      fellBack = true;
      if (hls) {
        hls.destroy();
        hls = null;
        hlsRef.current = null;
      }
      const time = el.currentTime;
      // A playing video starts again by onCanPlay, a video paused by the user stays paused.
      if (!el.paused) {
        startedRef.current = false;
      }
      if (time) {
        el.addEventListener('loadedmetadata', () => {
          el.currentTime = time;
        }, { once: true });
      }
      el.src = videoUrl;
    };

    if (!streamUrl) {
      el.src = videoUrl;
    } else if (el.canPlayType(HLS_TYPE)) {
      el.addEventListener('error', fallBack);
      el.src = streamUrl;
    } else if (Hls.isSupported()) {
      hls = new Hls(HLS_CONFIG);
      hlsRef.current = hls;
      if (!activeRef.current) {
        hls.config.maxBufferLength = PRELOAD_BUFFER_LENGTH;
        hls.config.maxMaxBufferLength = PRELOAD_MAX_BUFFER_LENGTH;
      }
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          fallBack();
        }
      });
      hls.loadSource(streamUrl);
      hls.attachMedia(el);
    } else {
      el.src = videoUrl;
    }

    return () => {
      el.removeEventListener('error', fallBack);
      hlsRef.current = null;
      if (hls) {
        hls.destroy();
        return;
      }
      // Stops the download of a native stream or an mp4 that leaves the window of the feed.
      el.removeAttribute('src');
      el.load();
    };
  }, [video.id, streamUrl, videoUrl]);

  // The muted attribute of React is only read once: the property is set here. A neighbour stays silent.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted || !active;
    }
  }, [active, muted, video.id]);

  // The current video buffers ahead normally, a neighbour only a few seconds (read live by hls.js).
  useEffect(() => {
    const hls = hlsRef.current;
    if (!hls) {
      return;
    }
    hls.config.maxBufferLength = active ? HLS_CONFIG.maxBufferLength : PRELOAD_BUFFER_LENGTH;
    hls.config.maxMaxBufferLength = active ? HLS_CONFIG.maxMaxBufferLength : PRELOAD_MAX_BUFFER_LENGTH;
  }, [active, video.id]);

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) {
      return;
    }
    if (el.paused) {
      el.play();
      setPaused(false);
    } else {
      el.pause();
      setPaused(true);
    }
  }, []);

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearHoldTimer, [clearHoldTimer]);

  // Story mode: the video pauses while the press is held (the buttons keep their own click).
  const onHoldStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!onZoneClick || paused || event.button !== 0 || target.closest('button')) {
      return;
    }
    heldRef.current = false;
    clearHoldTimer();
    holdTimerRef.current = setTimeout(() => {
      const el = videoRef.current;
      if (el && !el.paused) {
        heldRef.current = true;
        el.pause();
      }
    }, HOLD_DELAY);
  }, [clearHoldTimer, onZoneClick, paused]);

  const onHoldEnd = useCallback(() => {
    clearHoldTimer();
    const el = videoRef.current;
    if (heldRef.current && el && el.paused) {
      el.play().catch(() => setPaused(true));
    }
  }, [clearHoldTimer]);

  const onClickFrame = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    // A paused story (sound refused, last video ended) starts again on a click.
    if (!onZoneClick || paused) {
      togglePlay();
      return;
    }
    if (heldRef.current) {
      heldRef.current = false;
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    onZoneClick(event.clientX - rect.left < rect.width / 3 ? 'previous' : 'next');
  }, [onZoneClick, paused, togglePlay]);

  const handleEnded = useCallback(() => {
    setPaused(true);
    if (onEnded) {
      onEnded();
    }
  }, [onEnded]);

  const onTimeUpdate = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    const { currentTime, duration } = event.currentTarget;
    if (duration) {
      setProgress(currentTime / duration);
    }
  }, []);

  // The browser refuses the autoplay with sound before an action of the user: muted then.
  const start = useCallback((el: HTMLVideoElement) => {
    startedRef.current = true;
    // No autoplay for bots: the play button is shown.
    if (navigator.webdriver) {
      setPaused(true);
      return;
    }
    // After a reload nothing was clicked yet: the browser refuses the sound. Starting muted at once
    // avoids a refused play and a second try, the image stays still between both in some browsers.
    const activation = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
    if (activation && !activation.hasBeenActive && !el.muted) {
      el.muted = true;
      onForcedMute();
    }
    el.play().catch(() => {
      if (!el.muted) {
        el.muted = true;
        onForcedMute();
      }
      el.play().catch(() => setPaused(true));
    });
  }, [onForcedMute]);

  // canplay comes again after a seek: only the first one starts the video. A neighbour waits.
  const onCanPlay = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (startedRef.current || !activeRef.current) {
      return;
    }
    start(event.currentTarget);
  }, [start]);

  // Becoming the current one: a preloaded video starts at once (else at canplay).
  // Leaving it: paused and back to the start, ready if the user comes back.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) {
      return;
    }
    if (active) {
      if (!startedRef.current && el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        start(el);
      }
      return;
    }
    clearHoldTimer();
    heldRef.current = false;
    startedRef.current = false;
    el.pause();
    if (el.currentTime) {
      el.currentTime = 0;
    }
    setPaused(false);
    setProgress(0);
    setBuffering(true);
  }, [active, clearHoldTimer, start, video.id]);

  const onPlaying = useCallback(() => {
    setBuffering(false);
  }, []);

  const onWaiting = useCallback(() => {
    setBuffering(true);
  }, []);

  const onClickMute = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onToggleMute();
  }, [onToggleMute]);

  const onClickUser = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onOpenUser();
  }, [onOpenUser]);

  // The progress bar seeks: a click or a drag moves the video to that point.
  const seekTo = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const el = videoRef.current;
    if (!el || !el.duration) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    el.currentTime = ratio * el.duration;
    setProgress(ratio);
  }, []);

  const onSeekStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    seekTo(event);
  }, [seekTo]);

  const onSeekMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      seekTo(event);
    }
  }, [seekTo]);

  // The click of a seek must not pause the video.
  const stopClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  // A click on the rank explains the ranks (not the profile of the username next to it).
  const rankUser = useMemo(() => (video.user ? {
    id: video.user.id,
    username: video.user.username,
    rank: video.user.rank,
  } : undefined), [video.user]);

  const progressSx = useMemo(() => ({ ...styles.progressValue, width: `${progress * 100}%` }), [progress]);

  return (
    <Box
      sx={styles.frame}
      onClick={onClickFrame}
      onPointerDown={onHoldStart}
      onPointerUp={onHoldEnd}
      onPointerLeave={onHoldEnd}
    >
      {isLandscape && thumbnailUrl && <Box component="img" src={thumbnailUrl} alt="" sx={styles.blur} />}
      <Box
        component="video"
        key={video.id}
        ref={videoRef}
        loop={loop}
        playsInline
        preload="auto"
        onCanPlay={onCanPlay}
        onPlaying={onPlaying}
        onWaiting={onWaiting}
        onTimeUpdate={onTimeUpdate}
        onEnded={handleEnded}
        sx={isLandscape ? styles.videoLandscape : styles.video}
      />
      {renderOverlay && renderOverlay(progress)}
      {!renderOverlay && <VideoStatusPill video={video} />}
      <IconButton onClick={onClickMute} aria-label={muted ? t('admin.feed.sound') : t('admin.feed.mute')} sx={styles.sound}>
        {muted ? <VolumeOffOutlined /> : <VolumeUpOutlined />}
      </IconButton>
      {active && buffering && !paused && <CircularProgress size={34} sx={styles.buffering} />}
      {paused && (
        <Box sx={styles.paused}>
          <PlayArrow />
        </Box>
      )}
      <Box sx={styles.info}>
        <Box sx={styles.userRow}>
          <Box component="button" type="button" onClick={onClickUser} sx={styles.username}>
            {video.user ? video.user.username : ''}
          </Box>
          {rankUser && <RankBadge rank={rankUser.rank} user={rankUser} />}
        </Box>
        {video.description && <Box component="p" sx={styles.description}>{video.description}</Box>}
        {/* The game is in the action rail, next to the player. */}
        <VideoInfoLine video={video} hideGame />
        <Box
          role="slider"
          aria-label={t('admin.feed.seek')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          onPointerDown={onSeekStart}
          onPointerMove={onSeekMove}
          onClick={stopClick}
          sx={styles.seek}
        >
          <Box className="seek-track" sx={styles.progress}>
            <Box component="span" sx={progressSx} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export type { PlayerZone };

export default FeedPlayer;
